import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

// Add new server-sent event names here — nothing else needs to change
export const SSE_EVENTS = [
  'passwordChanged',
  'markedError',
  'updateProfile',
] as const;

export type SseEventType = (typeof SSE_EVENTS)[number];

export interface SseEvent<T = any> {
  type: SseEventType;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class SseService {
  constructor(private zone: NgZone) {}

  connect(userId: string): Observable<any> {
    return new Observable((observer) => {
      const eventSource = new EventSource(
        `${environment.API_URL}/v1/auth/events/${userId}`,
      );

      eventSource.onopen = () => {
        this.zone.run(() => {
          console.log('SSE connection established');
        });
      };

      // Default (unnamed) messages
      eventSource.onmessage = (event) => {
        this.zone.run(() => {
          observer.next(JSON.parse(event.data));
        });
      };

      // Named events — one listener per entry in SSE_EVENTS
      const listeners = SSE_EVENTS.map((type) => {
        const handler = (event: MessageEvent) => {
          this.zone.run(() => {
            observer.next({
              type,
              data: JSON.parse(event.data),
            } as SseEvent);
          });
        };
        eventSource.addEventListener(type, handler as EventListener);
        return { type, handler };
      });

      eventSource.onerror = (error) => {
        this.zone.run(() => {
          console.error('SSE connection error', error);
        });

        // Don't necessarily close here.
        // EventSource automatically attempts to reconnect.
      };

      return () => {
        listeners.forEach(({ type, handler }) =>
          eventSource.removeEventListener(type, handler as EventListener),
        );
        eventSource.close();
      };
    });
  }
}
